import { PaidSessionExplorerCard } from "../components/dashboard/PaidSessionExplorerCard.js";
import { PayersCard } from "../components/dashboard/PayersCard.js";
import { PaymentFlowFunnelCard } from "../components/dashboard/PaymentFlowFunnelCard.js";
import { RevenueByDayCard } from "../components/dashboard/RevenueByDayCard.js";
import { RevenueByHourCard } from "../components/dashboard/RevenueByHourCard.js";
import { RevenuePerTableByDayCard } from "../components/dashboard/RevenuePerTableByDayCard.js";
import { StintsToPaymentConfirmedCard } from "../components/dashboard/StintsToPaymentConfirmedCard.js";
import { KpiRow } from "../components/KpiRow.js";
import type { ChartTableSelection } from "../lib/chartTables.js";
import { formatGbpPence } from "../lib/formatMoney.js";
import type {
  DashboardTabHandler,
  FilterState,
  OpenRawEventsHandler,
  RetentionData,
} from "../types.js";

interface RetentionPanelProps {
  data: RetentionData | null;
  filters: FilterState;
  reportingTimezone: string;
  onOpenRawEvents: OpenRawEventsHandler;
  onOpenSessions: DashboardTabHandler;
  chartTables: ChartTableSelection;
  onChartTablesChange: (next: ChartTableSelection) => void;
  chartFullWeeks: boolean;
  onChartFullWeeksChange: (next: boolean) => void;
}

export function RetentionPanel({
  data,
  filters,
  reportingTimezone,
  onOpenRawEvents,
  onOpenSessions,
  chartTables,
  onChartTablesChange,
  chartFullWeeks,
  onChartFullWeeksChange,
}: RetentionPanelProps) {
  return (
    <>
      {!data ? (
        <p className="py-5 text-[13px] text-text-secondary">No data available</p>
      ) : (
        <>
          <KpiRow
            items={[
              {
                name: "Payments confirmed",
                value: String(data.eventCount),
                sub: "platform payment_confirmed",
              },
              {
                name: "Table payment_succeeded",
                value: String(data.tablePaymentSucceeded),
                sub: "shell telemetry (diagnostic)",
              },
              {
                name: "Gross Rev.",
                value: formatGbpPence(data.grossRevPence),
                sub:
                  data.unregisteredRevPence > 0
                    ? `incl. ${formatGbpPence(data.unregisteredRevPence)} shell telemetry gap`
                    : "from payment_confirmed amounts",
              },
            ]}
          />

          <div className="grid grid-cols-1 gap-[10px] xl:grid-cols-2">
            <div className="min-w-0">
              <RevenuePerTableByDayCard
                rows={data.revenueByDay}
                tableCount={data.revenueTableCount}
                devices={data.chartDevices}
                uptimeByDeviceDay={data.uptimeByDeviceDay}
                revenueByDeviceDay={data.revenueByDeviceDay}
                chartTables={chartTables}
                onChartTablesChange={onChartTablesChange}
                fullWeeks={chartFullWeeks}
                onFullWeeksChange={onChartFullWeeksChange}
                filters={filters}
              />
            </div>

            <PaymentFlowFunnelCard
              funnelDisplay={data.funnelDisplay}
              grossRevPence={data.grossRevPence}
              registeredPayments={data.registeredPayments}
              unregisteredPayments={data.unregisteredPayments}
              unregisteredRevPence={data.unregisteredRevPence}
              mobilePageviewEvents={data.mobilePageviewEvents}
              paymentAttemptedEvents={data.paymentAttemptedEvents}
              filters={filters}
              reportingTimezone={reportingTimezone}
              onOpenRawEvents={onOpenRawEvents}
              onOpenSessions={onOpenSessions}
              compact
            />

            <div className="min-w-0">
              <RevenueByDayCard
                rows={data.revenueByDay}
                tableCount={data.revenueTableCount}
                devices={data.chartDevices}
                uptimeByDeviceDay={data.uptimeByDeviceDay}
                revenueByDeviceDay={data.revenueByDeviceDay}
                chartTables={chartTables}
                onChartTablesChange={onChartTablesChange}
                fullWeeks={chartFullWeeks}
                onFullWeeksChange={onChartFullWeeksChange}
                filters={filters}
              />
            </div>

            <div className="min-w-0">
              <StintsToPaymentConfirmedCard
                rows={data.stintsToPaymentConfirmedByDay}
                tableCount={data.revenueTableCount}
                devices={data.chartDevices}
                uptimeByDeviceDay={data.uptimeByDeviceDay}
                stintsByDeviceDay={data.stintsByDeviceDay}
                paymentConfirmedByDeviceDay={data.paymentConfirmedByDeviceDay}
                chartTables={chartTables}
                onChartTablesChange={onChartTablesChange}
                fullWeeks={chartFullWeeks}
                onFullWeeksChange={onChartFullWeeksChange}
                filters={filters}
              />
            </div>

            <PayersCard data={data} onOpenRawEvents={onOpenRawEvents} compact />

            <div className="min-w-0">
              <RevenueByHourCard
                rows={data.revenueByHour}
                reportingTimezone={reportingTimezone}
              />
            </div>

            <div className="xl:col-span-2">
              <PaidSessionExplorerCard
                filters={filters}
                reportingTimezone={reportingTimezone}
                onOpenRawEvents={onOpenRawEvents}
              />
            </div>
          </div>
        </>
      )}

      {!data ? (
        <div className="mt-[10px]">
          <PaidSessionExplorerCard
            filters={filters}
            reportingTimezone={reportingTimezone}
            onOpenRawEvents={onOpenRawEvents}
          />
        </div>
      ) : null}
    </>
  );
}
